import {
	combine,
	withContext,
	withCallbacks,
	withStateHandlers,
	withEffect,
} from 'react-hooks-combine'
import { StoreContext } from 'store/context'
import { withRouter } from 'react-router'
import { ServicesContext } from 'services/context'
import fp from 'lodash/fp'
import { toaster } from 'utils/toast-wrapper'
import { showNotificationBasedOnDevice } from 'utils/notification-helper'
import { ROLES } from 'constants/account.roles'
import axios from 'axios'

import DonationReceiptsComponent from './component'

const DONATION_RECEIPTS_START_YEAR = process.env.REACT_APP_DONATION_RECEIPTS_START_YEAR || 2024

const initialLoadingStates = () => {
	const currentYear = new Date().getFullYear()
	const states = {}

	for (let year = currentYear; year >= DONATION_RECEIPTS_START_YEAR; year--) {
		states[year] = { download: false, email: false }
	}

	return states
}

export default combine({
	hocs: [withRouter],
	hooks: [
		withStateHandlers(
			{
				loadingStates: initialLoadingStates(),
				donationYears: [],
			},
			{
				setState: ({ args: [state] }) => state,
			}
		),
		withContext('appStore', StoreContext),
		withContext(
			'sponsorsService',
			ServicesContext,
			({ sponsorsService }) => sponsorsService
		),
		withContext(
			'memberService',
			ServicesContext,
			({ memberService }) => memberService
		),
		withContext('loggedEntity', StoreContext, fp.get('auth.loggedEntity')),

		withEffect(
			({ memberService, setState }) => {
				const fetchDonationYears = async () => {
					try {
						const donationAmounts = await memberService.getDonationAmounts()
						const years = Object.keys(donationAmounts)
							.filter(year => donationAmounts[year] > 0)
							.map(year => Number.parseInt(year, 10))
							.sort((a, b) => b - a) // Sort in descending order

						setState({ donationYears: years })
					} catch (error) {
						console.error('Failed to fetch donation years:', error)
					}
				}

				fetchDonationYears()
			},
			[]
		),

		withCallbacks(
			{
				onClickDownload:
					({ memberService, sponsorsService, setState, loggedEntity, loadingStates }) =>
						async (year, isEmailRequest = false) => {
							try {
								setState({
									loadingStates: {
										...loadingStates,
										[year]: {
											...loadingStates[year],
											[isEmailRequest ? 'email' : 'download']: true
										}
									}
								})

								if (loggedEntity.role === ROLES.SPONSOR) {
									await sponsorsService.downloadDonations(year)
									if (isEmailRequest) {
										const notification = {
											type: 'success',
											message: 'Email sent',
											timestamp: Date.now(),
											topOnly: true
										}
										localStorage.setItem('inwinNotification', JSON.stringify(notification))
									} else {
										const notification = {
											type: 'success',
											message: 'Download successful',
											timestamp: Date.now(),
											topOnly: true
										}
										localStorage.setItem('inwinNotification', JSON.stringify(notification))
									}
								} else {
									if (isEmailRequest) {
										await axios.post(`/member/report/donations/${year}`)
										const notification = {
											type: 'success',
											message: 'Email sent',
											timestamp: Date.now(),
											topOnly: true
										}
										localStorage.setItem('inwinNotification', JSON.stringify(notification))
									} else {
										await memberService.downloadDonations(year)
										const notification = {
											type: 'success',
											message: 'Download successful',
											timestamp: Date.now(),
											topOnly: true
										}
										localStorage.setItem('inwinNotification', JSON.stringify(notification))
									}
								}

								setState({
									loadingStates: {
										...loadingStates,
										[year]: {
											...loadingStates[year],
											[isEmailRequest ? 'email' : 'download']: false
										}
									}
								})
							} catch (error) {
								setState({
									loadingStates: {
										...loadingStates,
										[year]: {
											...loadingStates[year],
											[isEmailRequest ? 'email' : 'download']: false
										}
									}
								})
								const notification = {
									type: 'error',
									message: isEmailRequest ? 'Email not sent' : 'Download failed',
									timestamp: Date.now(),
									topOnly: true
								}
								localStorage.setItem('inwinNotification', JSON.stringify(notification))
							}
						},
			},
			['loadingStates']
		),
	],
	transformProps: ({
		onClickDownload,
		loadingStates,
		loggedEntity,
		donationYears,
		...rest
	}) => {
		return {
			...rest,
			onClickDownload,
			loggedEntity,
			loadingStates,
			donationYears,
		}
	},
})(DonationReceiptsComponent)
